import { Component, OnInit, OnDestroy, inject, Input, WritableSignal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { SharedModule } from '../../shared.module';
import { interval, Subject, takeUntil } from 'rxjs';
import { BannerStateService } from '../../../services/banner-state.service';
import {
  ImportProgressService,
  ImportProgressEvent,
  ImportProgressData
} from '../../../services/import-progress.service';
import { ElixirImportProgressService } from '../../../services/elixir-import-progress.service';
import { Store } from '@ngxs/store';
import { ShopState } from '../../../_state/shop/shop.state';
import { filter, take } from 'rxjs/operators';

interface ImportStep {
  id: string;
  name: string;
  status: 'pending' | 'waiting' | 'in-progress' | 'completed' | 'error' | 'skipped';
  progress: number;
  totalItems?: number;
  processedItems?: number;
  weight: number;
  dependencies?: string[];
  skipReason?: string;
}

@Component({
  selector: 'app-import-progress-modal',
  imports: [CommonModule, SharedModule, NgbProgressbarModule],
  templateUrl: './import-progress-modal.component.html',
  styleUrls: ['./import-progress-modal.component.scss']
})
export class ImportProgressModalComponent implements OnInit, OnDestroy {
  @Input() shopId?: string;

  activeModal = inject(NgbActiveModal);
  bannerState = inject(BannerStateService);
  private nodeImportService = inject(ImportProgressService);
  private elixirImportService = inject(ElixirImportProgressService);
  importProgressService: ImportProgressService | ElixirImportProgressService;
  store = inject(Store);

  private destroy$ = new Subject<void>();
  // CRITICAL: All 12 stages matching backend ProgressConfig
  importSteps: WritableSignal<ImportStep[]> = signal<ImportStep[]>([
    {
      id: 'products-catalog',
      name: 'Importing Products Catalog',
      status: 'pending',
      progress: 0,
      weight: 15
    },
    {
      id: 'products-details',
      name: 'Product Details & Images',
      status: 'pending',
      progress: 0,
      weight: 10
    },
    {
      id: 'products-inventory',
      name: 'Product Inventory',
      status: 'waiting',
      progress: 0,
      weight: 10,
      dependencies: ['products-catalog', 'products-details']
    },
    {
      id: 'warehouses',
      name: 'Importing Warehouses',
      status: 'pending',
      progress: 0,
      weight: 5
    },
    { id: 'orders', name: 'Importing Orders', status: 'pending', progress: 0, weight: 12 },
    {
      id: 'line-items-fallback',
      name: 'Line Items Sync',
      status: 'waiting',
      progress: 0,
      weight: 3,
      dependencies: ['orders']
    },
    {
      id: 'statements',
      name: 'Importing Statements',
      status: 'pending',
      progress: 0,
      weight: 5
    },
    {
      id: 'order-statements',
      name: 'Statement Transactions',
      status: 'waiting',
      progress: 0,
      weight: 12,
      dependencies: ['orders', 'statements']
    },
    {
      id: 'order-sku-transactions',
      name: 'Multi-SKU Transactions',
      status: 'waiting',
      progress: 0,
      weight: 3,
      dependencies: ['order-statements']
    },
    {
      id: 'refunds',
      name: 'Refunds & Returns',
      status: 'waiting',
      progress: 0,
      weight: 10,
      dependencies: ['orders']
    },
    { id: 'fbt', name: 'FBT Inventory', status: 'pending', progress: 0, weight: 5 },
    {
      id: 'analytics',
      name: 'Analytics & Metrics',
      status: 'waiting',
      progress: 0,
      weight: 10,
      dependencies: [
        'products-catalog',
        'orders',
        'statements',
        'order-statements',
        'order-sku-transactions'
      ]
    }
  ]);

  overallProgress: WritableSignal<number> = signal(0);
  currentStep = signal<string>('');
  progressMessage = signal<string>(''); // Dynamic message from backend
  estimatedCompletionTime: WritableSignal<number | null> = signal(null);

  constructor() {
    // Elixir is the only import backend
    this.importProgressService = this.elixirImportService;
  }

  ngOnInit(): void {
    if (!this.shopId) {
      const activeShop = this.store.selectSnapshot(ShopState.activeShop);
      if (activeShop) {
        this.shopId = activeShop.shopId || activeShop.id;
      }
    }

    if (!this.shopId) {
      console.warn('[ImportModal] ⚠️ No shop ID available - cannot track import progress');
      return;
    }

    this.importProgressService
      .getImportProgress()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.handleImportProgressEvent(event);
      });

    if (this.importProgressService.isConnected()) {
      this.importProgressService.subscribeToShop(this.shopId!);
    }

    this.importProgressService
      .getConnectionStatus()
      .pipe(
        filter(connected => connected),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.shopId) {
          this.importProgressService.subscribeToShop(this.shopId);
        }
      });

    this.loadCurrentProgress();
  }

  ngOnDestroy(): void {
    if (this.shopId) {
      this.importProgressService.unsubscribeFromShop(this.shopId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleImportProgressEvent(event: ImportProgressEvent): void {
    if (event.data.overall) {
      this.overallProgress.set(event.data.overall.progress);
      this.currentStep.set(event.data.overall.currentStep || '');
      // Use dynamic message from backend, fall back to currentStep if not available
      this.progressMessage.set(event.data.overall.message || event.data.overall.currentStep || '');
      this.estimatedCompletionTime.set(event.data.overall.estimatedCompletionTime || null);
    }

    // Handle all stages update (from initial load or full progress update)
    if (event.data.stages && event.data.stages.length > 0) {
      const currentSteps = this.importSteps();
      const updatedSteps = currentSteps.map(step => {
        const matched = event.data.stages!.find(s => s.id === step.id);

        if (matched) {
          const newStatus = this.determineStepStatus(step, matched.status, currentSteps);

          // CRITICAL FIX: For order-statements ONLY, don't set totalItems if it's 0, null, or undefined
          // This ensures UI shows "X processed" instead of "X / 0"
          // Other stages (like statements) should show "X / Y" format
          const totalItems =
            step.id === 'order-statements' &&
            (matched.totalItems === 0 ||
              matched.totalItems === null ||
              matched.totalItems === undefined)
              ? undefined
              : matched.totalItems;

          const processedItems = matched.processedItems;

          const updatedStep = {
            ...step,
            // Update name from displayName if provided (backend sends displayName)
            name: (matched as any).displayName || (matched as any).name || step.name,
            status: newStatus,
            progress: matched.progress,
            totalItems: totalItems,
            processedItems: processedItems,
            // Store message as skipReason when status is skipped
            skipReason:
              newStatus === 'skipped' && matched.message ? matched.message : step.skipReason
          };

          return updatedStep;
        }
        return step;
      });
      this.importSteps.set(updatedSteps);
    }

    // Handle single step update
    if (event.data.step) {
      const sd = event.data.step;

      this.updateStepProgress(sd.id, {
        status: sd.status as any,
        progress: sd.progress,
        totalItems: sd.totalItems,
        processedItems: sd.processedItems,
        message: sd.message
      });
    }

    // Handle core completion - just update UI but don't close modal
    if (event.type === 'import.core.completed') {
      this.currentStep.set('Core data imported, calculating metrics...');
      this.progressMessage.set('Core data imported successfully! Now calculating metrics...');
      // Do NOT close modal, import is still active
    }

    // Handle full completion - close modal and reload
    if (event.type === 'import.completed') {
      this.completeImport();
    }

    // Check waiting steps after any update
    // Disabled - let backend control waiting → in-progress transitions
    // this.checkAndUpdateWaitingSteps();

    // Update overall progress after handling events
    this.updateOverallProgress();
  }

  private async loadCurrentProgress(): Promise<void> {
    if (!this.shopId) return;

    // Load full progress from HTTP API - this always returns complete state with ALL stages
    this.importProgressService
      .getCurrentProgress(this.shopId)
      .pipe(take(1))
      .subscribe((progress: ImportProgressData | null) => {
        if (!progress) {
          console.warn('⚠️ [ImportModal] No progress data received from HTTP');
          return;
        }

        this.processLoadedProgress(progress);
      });
  }

  private processLoadedProgress(progress: ImportProgressData): void {
    // Add null checks for overall property
    if (progress.overall) {
      this.overallProgress.set(progress.overall.progress);
      this.currentStep.set(progress.overall.currentStep || '');
      // Use dynamic message from backend, fall back to currentStep if not available
      this.progressMessage.set(progress.overall.message || progress.overall.currentStep || '');
      this.estimatedCompletionTime.set(progress.overall.estimatedCompletionTime || null);
    } else {
      // Fallback when overall is missing
      this.overallProgress.set(0);
      this.currentStep.set('');
      this.progressMessage.set('Processing...');
      this.estimatedCompletionTime.set(null);
    }

    const currentSteps = this.importSteps();
    const updatedSteps = currentSteps.map(step => {
      const matched = progress.stages?.find(s => s.id === step.id);
      if (matched) {
        const newStatus = this.determineStepStatus(step, matched.status, currentSteps);

        // CRITICAL FIX: For order-statements ONLY, don't set totalItems if it's 0, null, or undefined
        // This ensures UI shows "X processed" instead of "X / 0"
        // Other stages (like statements) should show "X / Y" format
        const totalItems =
          step.id === 'order-statements' &&
          (matched.totalItems === 0 ||
            matched.totalItems === null ||
            matched.totalItems === undefined)
            ? undefined
            : matched.totalItems;

        const processedItems = matched.processedItems;

        const updatedStep = {
          ...step,
          // Update name from displayName if provided (backend sends displayName)
          name: (matched as any).displayName || (matched as any).name || step.name,
          status: newStatus,
          progress: matched.progress,
          totalItems: totalItems,
          processedItems: processedItems,
          // Store message as skipReason when status is skipped
          skipReason: newStatus === 'skipped' && matched.message ? matched.message : step.skipReason
        };

        return updatedStep;
      }
      return step;
    });

    this.importSteps.set(updatedSteps);

    // Update overall progress after loading
    this.updateOverallProgress();
  }

  private updateOverallProgress(): void {
    const sum = this.importSteps().reduce((acc, s) => acc + (s.progress / 100) * s.weight, 0);
    const progress = Math.min(Math.round(sum), 100);
    this.overallProgress.set(progress);

    // When progress reaches 100%, show finalizing message if not already completed
    if (progress === 100 && this.currentStep() !== 'Import completed successfully!') {
      this.currentStep.set('Finalizing import');
      this.progressMessage.set('Finalizing import');
    }
  }

  private checkAndUpdateWaitingSteps(): void {
    this.importSteps.update(steps => {
      return steps.map(step => {
        if (step.status === 'waiting' && step.dependencies) {
          // Check if all dependencies are completed
          const allDependenciesCompleted = step.dependencies.every(depId => {
            const depStep = steps.find(s => s.id === depId);
            return depStep && depStep.status === 'completed';
          });

          if (allDependenciesCompleted) {
            return { ...step, status: 'pending' };
          }
        }
        return step;
      });
    });
  }

  private determineStepStatus(
    step: ImportStep,
    backendStatus: string,
    allSteps: ImportStep[]
  ): 'pending' | 'waiting' | 'in-progress' | 'completed' | 'error' | 'skipped' {
    // CRITICAL: If backend status is empty, undefined, or invalid, keep current status or use fallback
    if (!backendStatus || backendStatus.trim() === '') {
      // Keep current status or fallback to waiting/pending based on dependencies
      return (
        step.status || (step.dependencies && step.dependencies.length > 0 ? 'waiting' : 'pending')
      );
    }

    // CRITICAL: Handle invalid statuses like "hidden" - convert to appropriate status
    if (backendStatus === 'hidden' || backendStatus === 'undefined' || backendStatus === 'null') {
      // For steps with dependencies, use waiting; otherwise pending
      return step.dependencies && step.dependencies.length > 0 ? 'waiting' : 'pending';
    }

    // If backend explicitly sets waiting, in-progress, completed, error, or skipped, use that
    if (['waiting', 'in-progress', 'completed', 'error', 'skipped'].includes(backendStatus)) {
      return backendStatus as any;
    }

    // CRITICAL: If step has dependencies and backend says pending, convert to waiting
    // This ensures components with dependencies show "waiting" status even if backend sends "pending"
    // The backend will change it to in-progress when it actually starts
    if (step.dependencies && step.dependencies.length > 0 && backendStatus === 'pending') {
      return 'waiting';
    }

    // Otherwise use backend status as-is
    return backendStatus as any;
  }

  private completeImport(): void {
    this.currentStep.set('Import completed successfully!');
    this.progressMessage.set('Import completed successfully!');
    this.overallProgress.set(100);
    // Wait 3 seconds to show success message (as per requirements)
    // This gives user time to see the completion state before modal closes
    setTimeout(() => {
      this.bannerState.setDataImportInProgress(false);
      this.activeModal.close('completed');
      // Reload the page to show updated data
      window.location.reload();
    }, 3000);
  }

  private handleImportError(error: string): void {
    this.currentStep.set('Import failed: ' + error);
    this.progressMessage.set('Import failed: ' + error);

    // Mark current step as error
    this.importSteps.update(steps =>
      steps.map(step => (step.status === 'in-progress' ? { ...step, status: 'error' } : step))
    );

    // Keep modal open for user to see the error
  }

  getStepIcon(step: ImportStep): string {
    switch (step.status) {
      case 'pending':
        return 'circle';
      case 'waiting':
        return 'clock';
      case 'in-progress':
        return 'loader';
      case 'completed':
        return 'check-circle';
      case 'error':
        return 'x-circle';
      case 'skipped':
        return 'skip-forward';
      default:
        return 'circle';
    }
  }

  getStepClass(step: ImportStep): string {
    // CRITICAL: Ensure status is defined, fallback to 'pending' or 'waiting' based on dependencies
    // Handle empty string, null, undefined, and invalid statuses like "hidden"
    const originalStatus = step.status;
    const validStatuses: Array<ImportStep['status']> = [
      'pending',
      'waiting',
      'in-progress',
      'completed',
      'error',
      'skipped'
    ];
    const hasValidStatus = originalStatus && validStatuses.includes(originalStatus);

    let status: ImportStep['status'];
    if (hasValidStatus) {
      status = originalStatus;
    } else {
      // For steps with dependencies, use waiting; otherwise pending
      status = step.dependencies && step.dependencies.length > 0 ? 'waiting' : 'pending';
    }

    return `step-${status}`;
  }

  getProgressType(progress: number): string {
    if (progress < 30) return 'danger';
    if (progress < 70) return 'warning';
    return 'success';
  }

  getWaitingMessage(step: ImportStep): string {
    // CRITICAL: Also handle "pending" status if step has dependencies
    // This handles cases where backend sends "pending" instead of "waiting"
    if ((step.status !== 'waiting' && step.status !== 'pending') || !step.dependencies) {
      return '';
    }

    const steps = this.importSteps();

    // Check if all dependencies are completed
    const allDependenciesCompleted = step.dependencies.every(depId => {
      const depStep = steps.find(s => s.id === depId);
      return depStep && depStep.status === 'completed';
    });

    if (allDependenciesCompleted) {
      return 'Ready to start, waiting for system...';
    }

    // Get names of incomplete dependencies
    const incompleteDependencies = step.dependencies
      .filter(depId => {
        const depStep = steps.find(s => s.id === depId);
        return depStep && depStep.status !== 'completed';
      })
      .map(depId => {
        const depStep = steps.find(s => s.id === depId);
        return depStep ? depStep.name : depId;
      });

    if (incompleteDependencies.length === 0) {
      return 'Waiting...';
    } else if (incompleteDependencies.length === 1) {
      return `Waiting for ${incompleteDependencies[0]}`;
    } else {
      const lastDep = incompleteDependencies.pop();
      return `Waiting for ${incompleteDependencies.join(', ')} and ${lastDep}`;
    }
  }

  getSkipReason(step: ImportStep): string {
    if (step.status !== 'skipped') {
      return '';
    }

    // Use provided skip reason if available
    if (step.skipReason) {
      return step.skipReason;
    }

    // Default skip reasons based on step ID
    switch (step.id) {
      case 'fbt':
        return 'No FBT access for this shop';
      case 'refunds':
        return 'No refunds found';
      case 'products-inventory':
        return 'Inventory data not available';
      case 'warehouses':
        return 'Already imported in previous period';
      default:
        return 'Not applicable';
    }
  }

  private updateStepProgress(
    stepId: string,
    progressData: {
      status: 'pending' | 'waiting' | 'in-progress' | 'completed' | 'error' | 'skipped';
      progress: number;
      totalItems?: number;
      processedItems?: number;
      message?: string;
    }
  ) {
    this.importSteps.update(steps => {
      return steps.map(step => {
        if (step.id !== stepId) return step;

        const newStatus = this.determineStepStatus(step, progressData.status, steps);

        // CRITICAL FIX: For order-statements ONLY, don't set totalItems if it's 0, null, or undefined
        // This ensures UI shows "X processed" instead of "X / 0"
        // Other stages (like statements) should show "X / Y" format
        const totalItems =
          step.id === 'order-statements' &&
          (progressData.totalItems === 0 ||
            progressData.totalItems === null ||
            progressData.totalItems === undefined)
            ? undefined
            : (progressData.totalItems ?? step.totalItems);

        const updatedStep = {
          ...step,
          status: newStatus,
          progress: progressData.progress,
          totalItems: totalItems,
          processedItems: progressData.processedItems ?? step.processedItems,
          // Store message as skipReason when status is skipped
          skipReason:
            newStatus === 'skipped' && progressData.message ? progressData.message : step.skipReason
        };
        return updatedStep;
      });
    });
  }

  getTimeEstimateMessage(): string {
    // Check overall progress first
    const progress = this.overallProgress();

    // If import is nearly complete (>= 95%), show special message
    if (progress >= 95) {
      return 'Your import ends soon';
    }

    const estimatedTime = this.estimatedCompletionTime();

    // If no estimate yet, use fallback based on total items
    if (!estimatedTime) {
      return this.getFallbackTimeMessage();
    }

    // Calculate remaining seconds
    const remainingMs = estimatedTime - Date.now();
    if (remainingMs <= 0) {
      return 'Completing shortly';
    }

    const remainingSeconds = Math.round(remainingMs / 1000);
    const remainingMinutes = remainingSeconds / 60;

    // Format based on time ranges
    if (remainingMinutes < 1) {
      return 'This should take less than a minute';
    } else if (remainingMinutes <= 5) {
      return 'This may take a few minutes';
    } else if (remainingMinutes <= 30) {
      return 'This may take several minutes';
    } else if (remainingMinutes <= 120) {
      return 'This may take up to 2 hours';
    } else {
      return 'This is a large import and may take several hours';
    }
  }

  private getFallbackTimeMessage(): string {
    // Calculate total items across all stages
    const steps = this.importSteps();
    let totalItems = 0;

    for (const step of steps) {
      if (step.totalItems) {
        totalItems += step.totalItems;
      }
    }

    // If we don't have total items yet, return generic message
    if (totalItems === 0) {
      return 'This may take several minutes';
    }

    // Return estimate based on total items
    if (totalItems < 100) {
      return 'This may take a few minutes';
    } else if (totalItems < 1000) {
      return 'This may take several minutes';
    } else if (totalItems < 10000) {
      return 'This may take up to an hour';
    } else {
      return 'This is a large import and may take several hours';
    }
  }
}
